import { useMemo, useState } from 'react';
import { ExerciseDetail } from '../components/ExerciseDetail';
import { ExerciseThumb } from '../components/ExerciseImages';
import { IconSearch, IconStar } from '../components/Icons';
import { Sheet } from '../components/Sheet';
import type { Exercise } from '../data/exercise-types';
import { EXERCISES } from '../data/exercises';
import { MUSCLES, MUSCLE_IDS, REGION_LABEL, type MuscleId, type Region } from '../data/muscles';
import { useApp } from '../store/AppStore';

type Filter = 'all' | 'available' | 'favorites' | Region;

const FILTERS: { id: Filter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'available', label: 'Can do today' },
  { id: 'favorites', label: 'Favourites' },
  { id: 'push', label: REGION_LABEL.push },
  { id: 'pull', label: REGION_LABEL.pull },
  { id: 'legs', label: REGION_LABEL.legs },
  { id: 'core', label: REGION_LABEL.core },
];

export function Library() {
  const { state } = useApp();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [muscle, setMuscle] = useState<MuscleId | null>(null);
  const [selected, setSelected] = useState<Exercise | null>(null);

  const available = useMemo(() => new Set(state.settings.equipment), [state.settings.equipment]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();

    return EXERCISES.filter((ex) => {
      if (q) {
        const haystack = [ex.name, ...(ex.aka ?? []), ...ex.primary.map((m) => MUSCLES[m].name)]
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }

      if (muscle && !ex.primary.includes(muscle) && !ex.secondary.includes(muscle)) return false;

      switch (filter) {
        case 'available':
          return ex.equipment.every((eq) => available.has(eq));
        case 'favorites':
          return state.exercisePrefs.favorites.includes(ex.id);
        case 'all':
          return true;
        default:
          return ex.primary.some((m) => MUSCLES[m].region === filter);
      }
    }).sort((a, b) => b.quality - a.quality || a.name.localeCompare(b.name));
  }, [query, filter, muscle, available, state.exercisePrefs.favorites]);

  return (
    <div className="stack">
      <div className="searchbar">
        <IconSearch />
        <input
          type="search"
          value={query}
          placeholder="Search exercises…"
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search exercises"
        />
      </div>

      <div className="chiprow">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            className={`chip${filter === f.id ? ' on' : ''}`}
            onClick={() => setFilter(f.id)}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="chiprow">
        <button
          className={`chip${muscle === null ? ' on' : ''}`}
          onClick={() => setMuscle(null)}
        >
          Any muscle
        </button>
        {MUSCLE_IDS.map((id) => (
          <button
            key={id}
            className={`chip${muscle === id ? ' on' : ''}`}
            onClick={() => setMuscle(muscle === id ? null : id)}
          >
            {MUSCLES[id].short}
          </button>
        ))}
      </div>

      <div className="row between tiny faint">
        <span>
          {results.length} exercise{results.length === 1 ? '' : 's'}
        </span>
        <span>{EXERCISES.length} in the library</span>
      </div>

      <div className="card flush">
        {results.length === 0 && (
          <div className="empty small">Nothing matches. Try a different filter.</div>
        )}
        {results.map((ex) => {
          const canDo = ex.equipment.every((eq) => available.has(eq));
          const isFav = state.exercisePrefs.favorites.includes(ex.id);
          return (
            <button className="list-item" key={ex.id} onClick={() => setSelected(ex)}>
              {state.settings.showImages && <ExerciseThumb exerciseId={ex.id} />}
              <div className="grow">
                <div className="row" style={{ gap: 6 }}>
                  <span style={{ fontWeight: 600 }}>{ex.name}</span>
                  {isFav && <IconStar size={13} filled className="muted" />}
                </div>
                <div className="tiny faint">
                  {ex.primary.map((m) => MUSCLES[m].short).join(', ')} · {ex.defaultSets} ×{' '}
                  {ex.repRange[0]}–{ex.repRange[1]}
                </div>
              </div>
              {!canDo && <span className="pill danger">no kit</span>}
            </button>
          );
        })}
      </div>

      <Sheet
        open={Boolean(selected)}
        title={selected?.name}
        onClose={() => setSelected(null)}
      >
        {selected && <ExerciseDetail exercise={selected} />}
      </Sheet>
    </div>
  );
}
